import type { ButtonHTMLAttributes, ReactNode } from 'react'

export function Screen({ children }: { children: ReactNode }) {
  // Antes llevaba un hueco enorme abajo para que la barra flotante no tapara la
  // última tarea. Ahora la barra ocupa su propio sitio y sobra.
  return <div className="mx-auto w-full max-w-lg px-4 pb-8 pt-5">{children}</div>
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-2xl border border-[var(--color-line)] bg-[var(--color-surface)] p-4 ${className}`}
    >
      {children}
    </div>
  )
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-2 mt-6 flex items-baseline justify-between">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]">
        {children}
      </h2>
      {action}
    </div>
  )
}

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-[var(--color-brand)] text-white',
  secondary: 'border border-[var(--color-line)] bg-[var(--color-surface)] text-[var(--color-ink)]',
  ghost: 'text-[var(--color-brand)]',
  danger: 'border border-[var(--color-bad)] text-[var(--color-bad)]',
}

export function Button({
  variant = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      {...props}
      // min-h-11: por debajo de ~44px los toques fallan de forma notable en iPhone.
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 text-[15px] font-semibold transition active:scale-[0.98] disabled:opacity-40 ${VARIANTS[variant]} ${className}`}
    />
  )
}

export function Banner({
  tone,
  title,
  children,
}: {
  tone: 'warn' | 'bad' | 'good' | 'info'
  title: string
  children?: ReactNode
}) {
  const colors = {
    warn: 'border-[var(--color-warn)]/45 bg-[var(--color-warn)]/12',
    bad: 'border-[var(--color-bad)]/45 bg-[var(--color-bad)]/12',
    good: 'border-[var(--color-good)]/45 bg-[var(--color-good)]/12',
    info: 'border-[var(--color-line)] bg-[var(--color-surface)]',
  }[tone]

  return (
    <div className={`mb-3 rounded-2xl border p-4 text-[15px] ${colors}`}>
      <strong className="mb-1 block">{title}</strong>
      {children}
    </div>
  )
}

export function Spinner({ label = 'Cargando…' }: { label?: string }) {
  return <p className="py-10 text-center text-sm text-[var(--color-muted)]">{label}</p>
}

export function ErrorNote({ error }: { error: unknown }) {
  const message =
    typeof error === 'string' ? error : error instanceof Error ? error.message : 'Algo ha fallado'
  return (
    <div className="mb-3 rounded-xl border border-[var(--color-bad)]/45 bg-[var(--color-bad)]/12 p-3 text-sm">
      {message}
    </div>
  )
}
